import { ExternalLink } from "lucide-react";

import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { sellTimeline, type SellStep, type SellStepId } from "../../utils/cryptoSellStatus";
import { TimelineSteps, type TimelineStep } from "../order/OrderTimeline";

/** A step reads as what is happening while it is in progress, and as what happened once it is done. */
const STEP_LABELS: Record<
  SellStepId,
  { current: keyof CryptoSwapsTranslations; other: keyof CryptoSwapsTranslations }
> = {
  created: { current: "sell_timeline_created", other: "sell_timeline_created" },
  received: { current: "sell_timeline_awaiting", other: "sell_timeline_received" },
  cop_sent: { current: "sell_timeline_paying", other: "sell_timeline_cop_sent" },
  credited: { current: "sell_timeline_crediting", other: "sell_timeline_credited" },
  expired: { current: "sell_timeline_expired", other: "sell_timeline_expired" },
  refunding: { current: "sell_timeline_refunding", other: "sell_timeline_refunding" },
  refunded: { current: "sell_timeline_refunded", other: "sell_timeline_refunded" },
};

export function SaleTimeline({ sale }: { sale: OtcSellOrder }) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const txUrl = labels.txUrl(sale.deposit.network, sale.deposit.tx_hash);
  const explorer = labels.networkRule(sale.deposit.network)?.explorer ?? "";

  const hint = (step: SellStep) => {
    const when = step.at ? formatDateTime(step.at, { locale: activeLanguage }) : null;
    if (step.id === "received" && step.state === "done" && txUrl && explorer) {
      return (
        <span className="flex flex-wrap items-center gap-x-2">
          {when}
          <a
            href={txUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            {t("sell_deposit_tx_link", { explorer })}
            <ExternalLink aria-hidden="true" className="size-3" />
          </a>
        </span>
      );
    }
    return when ?? undefined;
  };

  const steps: TimelineStep[] = sellTimeline(sale).map((step) => ({
    id: step.id,
    label: t(step.state === "current" ? STEP_LABELS[step.id].current : STEP_LABELS[step.id].other),
    hint: hint(step),
    state: step.state,
  }));

  return <TimelineSteps steps={steps} />;
}
